import { faArrowRight, faCalendar } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { format } from "date-fns";
import { useOutsideClick } from "myParkdUtils/customHooks";
import { getDateRangePickerLocale } from "myParkdUtils/dateRangePickerLocales"
import { getTodayDateAtNoon } from "myParkdUtils/dateRangePickerUtils";
import { JSX, useRef, useState } from "react";
import { DateRange, DayPicker } from "react-day-picker"
import { FormattedMessage } from "react-intl";
import { connect } from "react-redux";
import { ReduxState } from "types";


type ActiveInput = 'startDate' | 'endDate' | undefined;

export const getDisabledDays = (dateRange: DateRange, activeInput: ActiveInput) => {
    if (activeInput === 'endDate') {
        return {
            before: dateRange.from,
            after: getTodayDateAtNoon()
        }
    }

    return {
        after: getTodayDateAtNoon()
    }
}


export const DateRangePickerInline = (props: InlineDateRangePickerProps) => {

    const calenderRef = useRef(null);
    const [collapsed, setCollapsed] = useState<boolean>(true);
    const [dateRange, setDateRange] = useState<DateRange>({ from: props.startDate, to: props.endDate });
    const [activeInput, setActiveInput] = useState<ActiveInput>(undefined);

    useOutsideClick(calenderRef, () => closeCalendar());

    const onSelect = (triggerDate: Date) => {
        let { from, to } = dateRange;

        if (activeInput === 'startDate') {
            if (triggerDate > dateRange.to!) {
                from = triggerDate;
                to = triggerDate; // do not allow to set an endDate before the startDate
            }
            else {
                from = triggerDate;
            }
            setActiveInput('endDate');
        }
        if (activeInput === 'endDate') {
            to = triggerDate;
            closeCalendar();
        }

        setDateRange({ from, to });
        props.onDateRangeChanged({ from, to });
    }

    const onDateButtonClick = (activeInput: ActiveInput) => {
        setActiveInput(activeInput);
        setCollapsed(false);
    }

    const closeCalendar = () => {
        setCollapsed(true);
        setActiveInput(undefined);
    }

    return (
        <div className="drp-button-container">

            <button
                className={"btn-date-range-picker " + (activeInput === 'startDate' ? 'active' : '')}
                data-testid='btn-startdate'
                onClick={() => onDateButtonClick('startDate')}
            >
                <FontAwesomeIcon icon={faCalendar} size='sm' style={{ marginRight: '0.5em' }} />
                {dateRange?.from ? format(dateRange.from, 'dd/MM/yy') : <FormattedMessage id='PARKING_HISTORY_DATE_RANGE_PICKER_START_DATE' />}
            </button>

            <FontAwesomeIcon icon={faArrowRight} size='sm' style={{ marginLeft: '0.5em', marginRight: '0.5em' }} />

            <button
                className={"btn-date-range-picker " + (activeInput === 'endDate' ? 'active' : '')}
                data-testid='btn-enddate'
                onClick={() => onDateButtonClick('endDate')}
            >
                <FontAwesomeIcon icon={faCalendar} size='sm' style={{ marginRight: '0.5em' }} />
                {dateRange?.to ? format(dateRange.to, 'dd/MM/yy') : <FormattedMessage id='PARKING_HISTORY_DATE_RANGE_PICKER_END_DATE' />}
            </button>


            {!collapsed && (
                <>
                    <div className='drp-calendar-container' ref={calenderRef} data-testid="calendar-container">
                        <div>
                            <DayPicker
                                mode="range"
                                min={0}
                                locale={getDateRangePickerLocale(props.userLocale!)}
                                timeZone='Europe/Brussels'
                                navLayout='around'
                                weekStartsOn={1}
                                numberOfMonths={props.numberOfMonths}
                                animate
                                required
                                selected={dateRange}
                                onSelect={(selected: DateRange, triggerDate: Date) => onSelect(triggerDate)}
                                disabled={getDisabledDays(dateRange, activeInput)}
                            />
                        </div>

                        {props.bottomComponent && props.bottomComponent}

                    </div>
                </>
            )}
        </div>
    )
}


interface OwnProps {
    startDate: Date,
    endDate: Date,
    numberOfMonths: number,
    onDateRangeChanged: (range: DateRange) => void,
    bottomComponent?: JSX.Element
}
type StateProps = ReturnType<typeof mapStateToProps>;
export type InlineDateRangePickerProps = StateProps & OwnProps;

/* istanbul ignore next */
function mapStateToProps(state: ReduxState) {
    return {
        userLocale: state.user?.user?.locale,
    };
}

export default connect(mapStateToProps, null)(DateRangePickerInline);